import { Injectable } from '@angular/core';
import { SpinnerService } from '@core/services/spinner.service';
import { PatientAppIframeCommunicationService } from '@modules/patient/services/patient-app-iframe-communication.service';
import { PatientDisplayMode } from '@modules/patient/models/patient-display-mode.enum';
import { PatientDisplayModeService } from '@modules/patient/services/patient-display-mode.service';
import { PatientCardStateService } from '@modules/patient-card/services/patient-card-state.service';
import { patientSectionModeEnum } from '@modules/patient/models/patient-section-visibility.enum';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { Observable, of, EMPTY, combineLatest, defer, merge } from 'rxjs';
import { delay, map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class PatientAppLoadStateService {
	readonly isPatientAppError$: Observable<boolean> = this.patientDisplayModeService.displayMode$.pipe(
		switchMap(mode => {
			if (mode === PatientDisplayMode.PatientCard) {
				return this.patientCardStateService.isPatientCardError$;
			}

			return this.patientAppIframeCommunicationService.isPatientAppError$;
		})
	);

	readonly isPatientAppReady$: Observable<boolean> = this.patientDisplayModeService.displayMode$.pipe(
		switchMap(mode => {
			if (mode === PatientDisplayMode.PatientCard) {
				return this.patientCardStateService.isPatientCardReady$;
			}

			if (mode === PatientDisplayMode.UnifiedPatients) {
				return of(true);
			}

			// Legacy iframe mode — depends on patient section mode
			return this.patientQuery.patientSectionMode$.pipe(
				switchMap(sectionMode =>
					sectionMode === patientSectionModeEnum.InlinePatientApp
						? this.patientAppIframeCommunicationService.isPatientAppReady$
						: of(true)
				)
			);
		})
	);

	isPatientReadyAndLoaded$: Observable<boolean> = this.setPatientAppLoader().pipe(shareReplay({ bufferSize: 1, refCount: true }));

	constructor(
		private readonly patientQuery: PatientQuery,
		private readonly patientAppIframeCommunicationService: PatientAppIframeCommunicationService,
		private readonly patientCardStateService: PatientCardStateService,
		private readonly patientDisplayModeService: PatientDisplayModeService,
		private readonly shellQuery: ShellQuery,
		private readonly spinnerService: SpinnerService
	) {}

	private setPatientAppLoader(): Observable<boolean> {
		return merge(
			defer(() => {
				this.spinnerService.pin();

				return EMPTY;
			}),
			combineLatest([
				this.isPatientAppError$,
				this.isPatientAppReady$,
				this.shellQuery.isRxPending$,
				this.shellQuery.isLoading$
			]).pipe(
				map(([isError, isReady, isRxPending, isLoading]) => {
					const isLoaded = (isError || isReady) && !isRxPending && !isLoading;

					if (isLoaded) {
						this.spinnerService.unpin();

						return true;
					}

					return false;
				}),
				delay(500)
			)
		);
	}
}
